import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from 'contexts';
import {
   Wrench,
   Trash2,
   GitMerge,
   FileText,
   Building2,
   Database,
   Barcode,
   RefreshCw,
   Monitor,
   Printer,
   ShieldBan,
   BoxIcon,
} from 'lucide-react';
import BankReconciliation from './components/BankReconciliation';
import DeleteUnusedMastersModal from './components/DeleteUnusedMasters';
import MergeCodeForm from './components/MergeCodeForm';
import EwayBill from './components/EWayBill';
import BankTransferModal from './components/BankTransfer';
import BulkUpdateSuspenseAccount from './components/BulkUpdateSuspenseAccount';
import DynamicBarcodeForm from './components/DynamicBarcode';
import PrintBarcodeModal from './components/PrintBarcode';
import AccountModal from './components/AccountModal';
import {
   Box,
   useColorMode,
   useColorModeValue,
   useDisclosure,
   Skeleton,
   SkeletonText,
   VStack,
   HStack,
} from '@chakra-ui/react';
import OSCheck from '../companyProfile/OSCheck';
import { useBusinessType } from '../../../contexts';
import StockApprovel from './components/StockApprovel'

const isDev = process.env.REACT_APP_ENVIRONMENT === 'dev';

const profiles = [
   {
      id: 1,
      title: 'Delete Unused Master',
      description: 'Clean up unused master data from your system',
      componentType: 'DeleteUnusedMasters',
      icon: Trash2,
      color: 'from-red-500 to-pink-600',
      iconBg: 'bg-red-50 dark:bg-red-900/20',
      iconColor: 'text-red-600 dark:text-red-400',
   },
   {
      id: 2,
      title: 'Synced Un-Approved Entry',
      description: 'Manage synced entries pending approval',
      componentType: 'UnusedPurchaseInvoiceMaster',
      icon: RefreshCw,
      color: 'from-blue-500 to-cyan-600',
      iconBg: 'bg-blue-50 dark:bg-blue-900/20',
      iconColor: 'text-blue-600 dark:text-blue-400',
   },
   {
      id: 3,
      title: 'Merge Code',
      description: 'Combine duplicate codes and records',
      componentType: 'MergeCodeForm',
      icon: GitMerge,
      color: 'from-purple-500 to-indigo-600',
      iconBg: 'bg-purple-50 dark:bg-purple-900/20',
      iconColor: 'text-purple-600 dark:text-purple-400',
   },
   {
      id: 4,
      title: 'E-Way Bill',
      description: 'Generate and manage e-way bills',
      componentType: 'EwayBill',
      icon: FileText,
      color: 'from-green-500 to-emerald-600',
      iconBg: 'bg-green-50 dark:bg-green-900/20',
      iconColor: 'text-green-600 dark:text-green-400',
   },
   ...(isDev
      ? [
         {
            id: 5,
            title: 'Bank Import',
            description: 'Import bank statements and transactions',
            componentType: 'BankTransferModal',
            icon: Building2,
            color: 'from-orange-500 to-amber-600',
            iconBg: 'bg-orange-50 dark:bg-orange-900/20',
            iconColor: 'text-orange-600 dark:text-orange-400',
         },
         {
            id: 6,
            title: 'Bulk Update Suspense Account',
            description: 'Update suspense accounts in bulk',
            componentType: 'BulkUpdateSuspenseAccount',
            icon: Database,
            color: 'from-teal-500 to-cyan-600',
            iconBg: 'bg-teal-50 dark:bg-teal-900/20',
            iconColor: 'text-teal-600 dark:text-teal-400',
         },
      ]
      : []),
   {
      id: 7,
      title: 'Generate Dynamic Barcode',
      description: 'Generate custom barcodes dynamically',
      componentType: 'DynamicBarcodeForm',
      icon: Barcode,
      color: 'from-violet-500 to-purple-600',
      iconBg: 'bg-violet-50 dark:bg-violet-900/20',
      iconColor: 'text-violet-600 dark:text-violet-400',
   },
   {
      id: 8,
      title: 'Check OS',
      description: 'Check all OS',
      componentType: 'OSCheck',
      icon: Monitor,
      color: 'from-rose-500 to-gray-600',
      iconBg: 'bg-rose-50 dark:bg-rose-900/20',
      iconColor: 'text-rose-600 dark:text-rose-400',
   },
   {
      id: 9,
      title: 'Print Barcode',
      description: 'Print barcodes for products and items',
      componentType: 'PrintBarcode',
      icon: Printer,
      color: 'from-indigo-500 to-blue-600',
      iconBg: 'bg-indigo-50 dark:bg-indigo-900/20',
      iconColor: 'text-indigo-600 dark:text-indigo-400',
   },
   {
      id: 10,
      title: 'Entry Block Manager',
      description: 'Search entries by account and block or unblock them',
      componentType: 'AccountModal',
      icon: ShieldBan,
      color: 'from-amber-500 to-red-600',
      iconBg: 'bg-amber-50 dark:bg-amber-900/20',
      iconColor: 'text-amber-600 dark:text-amber-400',
   },
   {
      id: 11,
      title: 'Stock Approval',
      description: 'Manage stock approval',
      componentType: 'StockApprovel',
      icon: BoxIcon,
      color: 'from-teal-500 to-cyan-600',
      iconBg: 'bg-teal-50 dark:bg-teal-900/20',
      iconColor: 'text-teal-600 dark:text-teal-400',
   },
];

// Skeleton Loader Component
const CardSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   return (
      <div
         className={`group relative rounded-2xl shadow-md 
            ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
         <div className="relative p-6">
            {/* Icon Skeleton */}
            <div className="flex items-start justify-between mb-4">
               <Skeleton
                  width="56px"
                  height="56px"
                  borderRadius="12px"
                  startColor={isDark ? 'gray.600' : 'gray.200'}
                  endColor={isDark ? 'gray.700' : 'gray.300'}
               />
               <Skeleton
                  width="8px"
                  height="8px"
                  borderRadius="full"
                  startColor={isDark ? 'gray.600' : 'gray.200'}
                  endColor={isDark ? 'gray.700' : 'gray.300'}
               />
            </div>

            {/* Title Skeleton */}
            <Skeleton
               height="24px"
               width="70%"
               mb="8px"
               startColor={isDark ? 'gray.600' : 'gray.200'}
               endColor={isDark ? 'gray.700' : 'gray.300'}
            />

            {/* Description Skeleton */}
            <VStack spacing="6px" align="start" mb="16px">
               <Skeleton
                  height="12px"
                  width="100%"
                  startColor={isDark ? 'gray.600' : 'gray.200'}
                  endColor={isDark ? 'gray.700' : 'gray.300'}
               />
               <Skeleton
                  height="12px"
                  width="80%"
                  startColor={isDark ? 'gray.600' : 'gray.200'}
                  endColor={isDark ? 'gray.700' : 'gray.300'}
               />
            </VStack>

            {/* Button Skeleton */}
            <HStack spacing="8px">
               <Skeleton
                  height="16px"
                  width="80px"
                  startColor={isDark ? 'gray.600' : 'gray.200'}
                  endColor={isDark ? 'gray.700' : 'gray.300'}
               />
               <Skeleton
                  height="16px"
                  width="16px"
                  startColor={isDark ? 'gray.600' : 'gray.200'}
                  endColor={isDark ? 'gray.700' : 'gray.300'}
               />
            </HStack>
         </div>
      </div>
   );
};

// Header Skeleton Component
const HeaderSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   return (
      <div className="max-w-7xl px-2 sm:px-6 lg:px-8 py-3">
         <div className="flex items-center gap-3 mt-3">
            <Skeleton
               width="48px"
               height="48px"
               borderRadius="12px"
               startColor={isDark ? 'gray.600' : 'gray.200'}
               endColor={isDark ? 'gray.700' : 'gray.300'}
            />
            <div className="flex-1">
               <Skeleton
                  height="28px"
                  width="200px"
                  mb="8px"
                  startColor={isDark ? 'gray.600' : 'gray.200'}
                  endColor={isDark ? 'gray.700' : 'gray.300'}
               />
               <Skeleton
                  height="16px"
                  width="300px"
                  startColor={isDark ? 'gray.600' : 'gray.200'}
                  endColor={isDark ? 'gray.700' : 'gray.300'}
               />
            </div>
         </div>
      </div>
   );
};

const Card = () => {
   const location = useLocation();
   const { user } = useUser();
   const navigate = useNavigate();
   const { colorMode } = useColorMode();
   const { businessTypes } = useBusinessType();
   const isDark = colorMode === 'dark';
   const { isOpen: isOpenOsCheck, onOpen: onOpenOsCheck, onClose: onCloseOsCheck } = useDisclosure();
   const [selectedProfile, setSelectedProfile] = useState(null);
   const [isdynamicBarcode, setDynamicBarcode] = useState(false);
   const [isDeleteUnusedMasters, setIsDeleteUnusedMasters] = useState(false);
   const [isBankTransfer, setIsBankTransfer] = useState(false);
   const [isBulkUpdateSuspenseAccount, setIsBulkUpdateSuspenseAccount] = useState(false);
   const [isMergeCode, setIsMergeCode] = useState(false);
   const [iswarehouse, setIswarehouse] = useState(false);
   const [isEwayBill, setIsEwayBill] = useState(false);
   const [isPrintBarcode, setIsPrintBarcode] = useState(false);
   const [isAccountModal, setIsAccountModal] = useState(false);
   const [isLoading, setIsLoading] = useState(true);
   const scrollbarColor = useColorModeValue('gray.300', 'gray.600');

   const utilityRights = user?.userRights?.find((ur) => ur?.companyId === user?.loginhistory?.companyId);

   useEffect(() => {
      // Simulate initial data loading
      const loadInitialData = async () => {
         try {
            // You can add any actual data fetching logic here
            // For now, we'll use a minimal timeout to ensure smooth rendering
            await new Promise((resolve) => setTimeout(resolve, 100));
         } catch (error) {
            console.error('Error loading data:', error);
         } finally {
            setIsLoading(false);
         }
      };

      loadInitialData();
   }, []);

   // Filter profiles based on admin status
   const visibleProfiles = profiles.filter((profile) => {
      // Check role-based utility permissions mapping (Admins bypass this restriction)
      if (!user?.isAdmin && !utilityRights?.utility?.[profile.title]) {
         return false;
      }
      if (profile.id === 5 && !user?.packageData?.[0]?.bank_import) {
         return false;
      }
      if (profile.id === 6 && !user?.packageData?.[0]?.bulk_update_suspense_account) {
         return false;
      }
      if (profile.id === 1 && !user?.packageData?.[0]?.delete_unused_master) {
         return false;
      }
      if (profile.id === 9 && !user?.packageData?.[0]?.print_barcode) {
         return false;
      }
      if (profile.id === 4 && !user?.packageData?.[0]?.multiple_eway_bill_generate) {
         return false;
      }
      if (profile.id === 7 && businessTypes?.businessType !== 'MOBILE') {
         return false;
      }
      return true;
   });

   const handleOpenComponent = (profile) => {
      if (isLoading) return; // Prevent clicks during loading

      setSelectedProfile(profile);

      if (profile.componentType === 'DeleteUnusedMasters') {
         setIsDeleteUnusedMasters(true);
      } else if (profile.componentType === 'MergeCodeForm') {
         setIsMergeCode(true);
      } else if (profile.componentType === 'EwayBill') {
         setIsEwayBill(true);
      } else if (profile.componentType === 'UnusedPurchaseInvoiceMaster') {
         navigate('/admin/utility/synced-unapproved-entry');
      } else if (profile.componentType === 'BankTransferModal') {
         setIsBankTransfer(true);
      } else if (profile.componentType === 'BulkUpdateSuspenseAccount') {
         setIsBulkUpdateSuspenseAccount(true);
      } else if (profile.componentType === 'DynamicBarcodeForm') {
         setDynamicBarcode(true);
      } else if (profile.componentType === 'OSCheck') {
         onOpenOsCheck();
      } else if (profile.componentType === 'PrintBarcode') {
         setIsPrintBarcode(true);
      } else if (profile.componentType === 'AccountModal') {
         setIsAccountModal(true);
      } else if (profile.componentType === 'StockApprovel') {
         navigate('/admin/utility/stock-approvel');
      }
   };

   const handleCloseModals = () => {
      setIsDeleteUnusedMasters(false);
      setIsMergeCode(false);
      setIswarehouse(false);
      setIsEwayBill(false);
      setSelectedProfile(null);
      setIsBankTransfer(false);
      setIsBulkUpdateSuspenseAccount(false);
      setDynamicBarcode(false);
      setIsPrintBarcode(false);
      setIsAccountModal(false);
   };

   const handleDeleteUnusedMasters = () => {
      // Implementation for handling delete unused masters
   };

   const handleBankTransfer = () => {
      // Implementation for handling delete unused masters
   };

   const handleBulkUpdateSuspenseAccount = () => {
      // Implementation for handling delete unused masters
   };

   const handleRenumberingTransection = () => {
      // Implementation for handling renumbering transaction
   };

   const handleMergeCodeForm = () => {
      // Implementation for handling merge code form
   };

   const handleEwayBill = () => {
      // Implementation for handling merge code form
   };

   const handleDynamicBarcode = () => {
      // Implementation for handling merge code form
   };

   return (
      <>
         {/* Main Container */}
         <Box
            height="100vh"
            width="100%"
            overflowY="auto"
            overflowX="hidden"
            sx={{
               '&::-webkit-scrollbar': {
                  width: { base: '4px', md: '6px' },
               },
               '&::-webkit-scrollbar-track': {
                  background: 'transparent',
               },
               '&::-webkit-scrollbar-thumb': {
                  background: scrollbarColor,
                  borderRadius: '10px',
               },
               '&::-webkit-scrollbar-thumb:hover': {
                  background: useColorModeValue('gray.400', 'gray.500'),
               },
               scrollbarWidth: 'thin',
               scrollbarColor: `${scrollbarColor} transparent`,
            }}
            pt={{ base: '60px', md: '30px', lg: '50px' }}
            px={{ base: 3, md: 4, lg: 2 }}
            pb={{ base: '20px', md: '30px' }}>
            <div>
               {/* Header Section */}
               <div className={``}>
                  {isLoading ? (
                     <HeaderSkeleton />
                  ) : (
                     <div className="max-w-7xl px-2 sm:px-6 lg:px-8 py-3">
                        <div className="flex items-center gap-3 mt-3">
                           <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg">
                              <Wrench className="w-6 h-6 text-white" />
                           </div>
                           <div>
                              <h1
                                 className={`text-2xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'
                                    }`}>
                                 Utility Tools
                              </h1>
                              <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                 Manage and maintain your system efficiently
                              </p>
                           </div>
                        </div>
                     </div>
                  )}
               </div>

               {/* Main Content */}
               <div className="max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
                  {/* Cards Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                     {isLoading
                        ? // Show skeleton loaders while loading
                        Array.from({ length: visibleProfiles.length }).map((_, index) => <CardSkeleton key={index} />)
                        : // Show actual cards when not loading
                        visibleProfiles.map((profile) => {
                           const Icon = profile.icon;
                           return (
                              <div
                                 key={profile.id}
                                 onClick={() => handleOpenComponent(profile)}
                                 className={`group relative rounded-2xl shadow-md hover:shadow-2xl 
                                       transition-all duration-300 cursor-pointer overflow-hidden
                                       ${isDark
                                       ? 'bg-gray-800 border border-gray-700 hover:border-blue-500'
                                       : 'bg-white border border-gray-200 hover:border-blue-300'
                                    }`}>
                                 {/* Gradient overlay on hover */}
                                 <div
                                    className={`absolute inset-0 bg-gradient-to-br ${profile.color} 
                                    ${isDark ? 'opacity-0 group-hover:opacity-20' : 'opacity-0 group-hover:opacity-10'} 
                                    transition-opacity duration-300`}
                                 />

                                 {/* Card Content */}
                                 <div className="relative p-6">
                                    {/* Icon */}
                                    <div className="flex items-start justify-between mb-4">
                                       <div
                                          className={`${profile.iconBg} p-3 rounded-xl group-hover:scale-110 transition-transform duration-300 shadow-sm`}>
                                          <Icon className={`w-7 h-7 ${profile.iconColor}`} />
                                       </div>
                                       {/* Decorative dot */}
                                       <div
                                          className={`w-2 h-2 rounded-full transition-colors duration-300 
                                       ${isDark ? 'bg-gray-600' : 'bg-gray-300'} 
                                       group-hover:bg-gradient-to-r group-hover:from-blue-500 group-hover:to-purple-600`}
                                       />
                                    </div>

                                    {/* Title */}
                                    <h3
                                       className={`text-lg font-bold mb-2 transition-all duration-300
                                    ${isDark ? 'text-white' : 'text-gray-800'}
                                    group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r 
                                    group-hover:from-blue-600 group-hover:to-purple-600`}>
                                       {profile.title}
                                    </h3>

                                    {/* Description */}
                                    <p
                                       className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'
                                          }`}>
                                       {profile.description}
                                    </p>

                                    {/* Arrow indicator */}
                                    <div
                                       className={`mt-4 flex items-center text-sm font-medium transition-colors duration-300
                                    ${isDark
                                             ? 'text-gray-400 group-hover:text-blue-400'
                                             : 'text-gray-500 group-hover:text-blue-600'
                                          }`}>
                                       <span>Open tool</span>
                                       <svg
                                          className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor">
                                          <path
                                             strokeLinecap="round"
                                             strokeLinejoin="round"
                                             strokeWidth={2}
                                             d="M9 5l7 7-7 7"
                                          />
                                       </svg>
                                    </div>
                                 </div>

                                 {/* Bottom accent line */}
                                 <div className="absolute bottom-0 left-0 right-0">
                                    <div
                                       className={`h-1 bg-gradient-to-r ${profile.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`}
                                    />
                                 </div>
                              </div>
                           );
                        })}
                  </div>
               </div>
            </div>
            <PrintBarcodeModal isPrintBarcode={isPrintBarcode} onClose={handleCloseModals} />
            <AccountModal isAccountModal={isAccountModal} onClose={handleCloseModals} />
         </Box>

         <OSCheck isOpen={isOpenOsCheck} onClose={onCloseOsCheck} />

         {/* Render the modals - All existing functionality preserved */}
         <DeleteUnusedMastersModal
            isDeleteUnusedMasters={isDeleteUnusedMasters}
            onClose={handleCloseModals}
            handleDeleteUnusedMasters={handleDeleteUnusedMasters}
         />

         <BankTransferModal
            isBankTransfer={isBankTransfer}
            onClose={handleCloseModals}
            handleBankTransfer={handleBankTransfer}
         />

         <BulkUpdateSuspenseAccount
            isBulkUpdateSuspenseAccount={isBulkUpdateSuspenseAccount}
            onClose={handleCloseModals}
            handleBulkUpdateSuspenseAccount={handleBulkUpdateSuspenseAccount}
         />

         <MergeCodeForm
            isMergeCode={isMergeCode}
            onClose={handleCloseModals}
            handleMergeCodeForm={handleMergeCodeForm}
         />

         <EwayBill isEwayBill={isEwayBill} onClose={handleCloseModals} handleEwayBill={handleEwayBill} />

         <DynamicBarcodeForm
            isdynamicBarcode={isdynamicBarcode}
            onClose={handleCloseModals}
            handleDynamicBarcode={handleDynamicBarcode}
         />
      </>
   );
};

export default Card;
